import posthog from 'posthog-js';

import { POSTHOG_APP_ID, POSTHOG_HOST, PRODUCTION } from '@/store/constants';
import { getViewportBand } from '@/services/viewport';
import { hydrateFeatureFlags } from '@/store/feature_flags';

const isPosthogDebug = () => new URLSearchParams(window.location.search).get('posthog_debug') === 'true';

let initialized = false;

// Last value written to the `viewport_band` super property. Re-registering is guarded on
// an actual band change: `register` persists to storage, and dragging a window across one
// boundary fires hundreds of resize events.
let registeredBand = null;

const trackViewportBandChange = () => {
    try {
        const band = getViewportBand(window.innerWidth);
        if (!band || band === registeredBand) return;
        registeredBand = band;
        posthog.register({ viewport_band: band });
    } catch (e) {
        // Non-fatal: tracking must never break a resize.
    }
};

// True once posthog.init ran this session. Identify/tracking helpers gate on
// this instead of PRODUCTION so staging (flags-only mode) still attaches
// identities for feature-flag targeting.
export const isPosthogInitialized = () => initialized;

// posthog-js persists identity in localStorage under ph_<token>_posthog; read
// the distinct id before init so the backend evaluates anonymous visitors
// under their existing id (stable percentage rollouts across reloads).
export function persistedDistinctId() {
    try {
        const raw = window.localStorage.getItem(`ph_${POSTHOG_APP_ID}_posthog`);
        return raw ? JSON.parse(raw).distinct_id || null : null;
    } catch (e) {
        return null;
    }
}

/**
 * Initialize PostHog whenever a project key is configured (staging/prod).
 * Event capture (autocapture/pageviews/recordings) stays production-only —
 * non-prod envs run in flags-only mode. `bootstrap` carries backend-evaluated
 * flags + distinct id (from hydrateFeatureFlags) so the first paint agrees
 * with the server. Returns whether PostHog is now initialized.
 */
export function initPosthog(app, bootstrap = null) {
    if (!POSTHOG_APP_ID || initialized) {
        return initialized;
    }
    const captureEnabled = PRODUCTION || isPosthogDebug();
    try {
        posthog.init(POSTHOG_APP_ID, {
            api_host: POSTHOG_HOST,
            ...(bootstrap ? { bootstrap } : {}),
            ...(captureEnabled
                ? {}
                : {
                      autocapture: false,
                      capture_pageview: false,
                      capture_pageleave: false,
                      disable_session_recording: true,
                  }),
            // Tag every event this app emits (named events, autocapture, $pageview,
            // $identify) as product telemetry via a super property. Events arriving
            // at PostHog without the property are defaulted to 'growth' at ingestion
            // — see docs/features/product-analytics.md. Registered inside `loaded`
            // rather than after init: posthog-js only guarantees `loaded` runs
            // before the initial $pageview capture — that the capture is deferred
            // past init() returning is an implementation detail of the version.
            //
            // `viewport_band` rides along (ENG-2840) so every event can be broken
            // down by width. It can't be set from `isPosthogInitialized()`-guarded
            // code: that flag is only raised after init() returns, i.e. after this
            // hook and possibly after the first $pageview.
            loaded: ph => {
                registeredBand = getViewportBand(window.innerWidth);
                ph.register({ tracking_source: 'product', viewport_band: registeredBand });
                window.addEventListener('resize', trackViewportBandChange, { passive: true });
            },
            session_recording: {
                // posthog-js masks typed INPUT values by default (maskAllInputs), but not
                // rendered text — and the onboarding surfaces print every answer the wizard
                // collected (legal names, DOBs, emails, beneficiaries, trust names) as plain
                // page text, which replay would otherwise capture verbatim. Elements tagged
                // `.ph-mask-text` record masked while their labels stay readable, so a
                // drop-off replay still shows which field the user was looking at. Applied in
                // components/onboarding/: IntakeReviewSummary, OnboardingPersonSelect (the
                // address-book rows and the selected-person card), OnboardingDetailsSlant.
                //
                // Attributes are NOT covered: rrweb serialises them verbatim, so a person's
                // name in an aria-label/title/alt reaches the replay even inside a masked
                // element. Keep names out of attributes rather than adding a class here.
                maskTextSelector: '.ph-mask-text',
                blockSelector: [
                    'iframe[src*="facebook.com"]',
                    'iframe[src*="linkedin.com"]',
                    'iframe[src*="ads.linkedin.com"]',
                    'iframe[src*="clarity.ms"]',
                    'iframe[src*="twitter.com"]',
                    'iframe[src*="intercom"]',
                    'iframe[name="sl-embed"]',
                ].join(', '),
            },
        });
        // Live flag toggles while the app is open. PostHog's browser payload
        // OMITS a disabled flag rather than reporting it false (verified against
        // prod), so merging that payload could only ever turn flags ON — switch
        // one off and the SPA kept its previous value until a reload, rendering
        // controls every backend enforcement path denied. Asking posthog-js
        // per key doesn't help either: isFeatureEnabled answers `undefined`, not
        // false, for a flag absent from the payload.
        //
        // So re-read the backend's map on the change signal instead. It
        // evaluates locally and answers with an explicit boolean for every
        // registry key (feature_flags.all_flags — the absent≠false fix from
        // ENG-2796), making it the only source that can say "off", and it keeps
        // the live answer identical to the one a reload would produce. Failures
        // apply nothing, so a dead backend can't blank a default-on kill switch.
        //
        // The first callback is init replaying the bootstrap we already applied
        // pre-mount, so skip it rather than immediately refetching it.
        let skipBootstrapReplay = Boolean(bootstrap);
        posthog.onFeatureFlags(() => {
            if (skipBootstrapReplay) {
                skipBootstrapReplay = false;
                return;
            }
            hydrateFeatureFlags(posthog.get_distinct_id());
        });
        initialized = true;
        app.config.globalProperties.$posthog = posthog;
        if (captureEnabled && !PRODUCTION) {
            console.log('[PostHog Debug] PostHog initialized for testing');
        }
    } catch (e) {
        // Non-fatal: the app runs on bootstrap/default flag values.
        console.error('[posthog] init failed', e);
    }
    return initialized;
}
